import React from "react";
import {
  Box,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { publicAxios as axios } from "../../../config/axios";
import endpoints from "../../../utils/endpoints";

type CboDispatch = {
  dispatchedQuantity?: number;
};

type CboRow = {
  supplierBusinessName: string;
  totalCBOQuantity?: number;
  dispatched?: CboDispatch[];
  invoiceQuantity?: number;
  underPickingQuantity?: number;
  allocatedQuantity?: number;
  backOrderQuantity?: number;
  advancedOrderQuantity?: number;
};

const getDispatchTotal = (row: CboRow) =>
  (row.dispatched || []).reduce(
    (sum, item) => sum + Number(item?.dispatchedQuantity || 0),
    0,
  );

const CboDetailsPopoverContent = ({ partNumber }: { partNumber: string }) => {
  const { data, isFetching, error } = useQuery(
    ["cbo-popover-by-part", partNumber],
    async () => {
      const { data: response } = await axios.get(
        endpoints.getCboByPartNumbers([partNumber]),
        {
          headers: {
            Authorization: localStorage.getItem("auth_orca"),
          },
        },
      );

      const rawResults = Array.isArray(response?.results)
        ? response.results
        : Array.isArray(response?.data?.results)
          ? response.data.results
          : [];
      const group = rawResults.find(
        (item: any) => item?.partNumber === partNumber,
      );
      return Array.isArray(group?.documents) ? group.documents : [];
    },
    {
      enabled: Boolean(partNumber),
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
    },
  );

  if (isFetching) {
    return (
      <Box p={3} minW="300px">
        <Spinner size="sm" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} minW="300px">
        <Text fontSize="xs" color="red.500">
          Failed to load CBO details
        </Text>
      </Box>
    );
  }

  const rows: CboRow[] = Array.isArray(data) ? data : [];
  if (rows.length === 0) {
    return (
      <Box p={3} minW="300px">
        <Text fontSize="xs" color="gray.600">
          No CBO details found.
        </Text>
      </Box>
    );
  }

  const totals = rows.reduce(
    (acc, row) => ({
      cbo: acc.cbo + Number(row.totalCBOQuantity || 0),
      dispatched: acc.dispatched + getDispatchTotal(row),
      invoiced: acc.invoiced + Number(row.invoiceQuantity || 0),
      underPicking: acc.underPicking + Number(row.underPickingQuantity || 0),
      allocated: acc.allocated + Number(row.allocatedQuantity || 0),
      backOrder: acc.backOrder + Number(row.backOrderQuantity || 0),
      advanceOrder: acc.advanceOrder + Number(row.advancedOrderQuantity || 0),
    }),
    {
      cbo: 0,
      dispatched: 0,
      invoiced: 0,
      underPicking: 0,
      allocated: 0,
      backOrder: 0,
      advanceOrder: 0,
    },
  );

  return (
    <Box p={2}>
      <TableContainer overflowX="auto" maxW="100%">
        <Table size="sm" variant="unstyled" minW="760px">
          <Thead bg="gray.100">
            <Tr>
              <Th textAlign="left" fontSize="xs" color="gray.700">
                Supplier Name
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                CBO
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                Dispatched
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                Invoiced
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                Under Picking
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                Allocated
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                Back Order
              </Th>
              <Th textAlign="center" fontSize="xs" color="gray.700">
                Advance Order
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, idx) => (
              <Tr
                key={`${row.supplierBusinessName}-${idx}`}
                bg={idx % 2 ? "gray.50" : "white"}
              >
                <Td fontWeight="700" color="gray.700" fontSize="sm">
                  {row.supplierBusinessName}
                </Td>
                <Td
                  textAlign="center"
                  fontWeight="700"
                  color="#2F9E77"
                  fontSize="xs"
                >
                  {Number(row.totalCBOQuantity || 0)}
                </Td>
                <Td textAlign="center" fontSize="xs">
                  {getDispatchTotal(row)}
                </Td>
                <Td textAlign="center" fontSize="xs">
                  {Number(row.invoiceQuantity || 0)}
                </Td>
                <Td textAlign="center" fontSize="xs">
                  {Number(row.underPickingQuantity || 0)}
                </Td>
                <Td textAlign="center" fontSize="xs">
                  {Number(row.allocatedQuantity || 0)}
                </Td>
                <Td
                  textAlign="center"
                  fontWeight="700"
                  color="#C95A8C"
                  fontSize="xs"
                >
                  {Number(row.backOrderQuantity || 0)}
                </Td>
                <Td
                  textAlign="center"
                  fontWeight="700"
                  color="#64AFA0"
                  fontSize="xs"
                >
                  {Number(row.advancedOrderQuantity || 0)}
                </Td>
              </Tr>
            ))}
            <Tr bg="gray.100">
              <Td fontWeight="800" fontSize="sm" color="gray.700">
                Total
              </Td>
              <Td
                textAlign="center"
                fontWeight="800"
                color="#2F9E77"
                fontSize="xs"
              >
                {totals.cbo}
              </Td>
              <Td textAlign="center" fontWeight="800" fontSize="xs">
                {totals.dispatched}
              </Td>
              <Td textAlign="center" fontWeight="800" fontSize="xs">
                {totals.invoiced}
              </Td>
              <Td
                textAlign="center"
                fontWeight="800"
                color="#A8792A"
                fontSize="xs"
              >
                {totals.underPicking}
              </Td>
              <Td textAlign="center" fontWeight="800" fontSize="xs">
                {totals.allocated}
              </Td>
              <Td
                textAlign="center"
                fontWeight="800"
                color="#C95A8C"
                fontSize="xs"
              >
                {totals.backOrder}
              </Td>
              <Td
                textAlign="center"
                fontWeight="800"
                color="#64AFA0"
                fontSize="xs"
              >
                {totals.advanceOrder}
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CboDetailsPopoverContent;
